// `cloudgrid init <kind> <name>` — registers a server-side entity in
// 'charged' state and scaffolds local files. Per spec v17 §6 and decision
// 015 (`docs/decisions/015-service-scaffolding-and-dev-runner.md` §"Scaffold
// commands") and the canonical happy path in
// `docs/strategy/developer-flow.md` Step 2.
//
// Filesystem effects on the success path:
//   1. Writes `.cloudgrid/link.json` (entity binding, gitignored).
//   2. Writes `cloudgrid.yaml` with `name: <slug>` and `services: {}`.
//   3. When `--type <T>` is passed, scaffolds `services/web/` from the
//      per-type starter templates so the user can `cloudgrid dev`
//      immediately. Reuses the same `scaffoldService` + `addService`
//      helpers `cloudgrid scaffold service` calls — one source of truth.
//   4. Appends `.cloudgrid/`, `.cloudgrid-dev.lock`, and `.env.local` to
//      `.gitignore` (`.env.local` per decision 018 — local-dev dotenv).
//
// Errors here are CloudGridError instances. Commander's action handler in
// index.ts already catches these and exits with code 1 (see index.ts:38).

import { Command } from 'commander';
import { existsSync, mkdirSync, readFileSync, writeFileSync } from 'fs';
import { dirname, join, relative, resolve } from 'path';
import { fileURLToPath } from 'url';
import { CloudGridError } from '../utils.js';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { getActiveOrg, notSignedInMessage } from '../active-org.js';
import { addGridOption, gridSlug, type GridOpts } from '../grid-opt.js';
import {
  appendGitignoreLine,
  writeLink,
  type LinkArtifact,
} from '../services/link.js';
import { addService, setExposeFalse } from './scaffold/yaml-edit.js';
import { scaffoldService } from './scaffold/scaffolder.js';
import type { ServiceType } from './scaffold/templates.js';
// BL621 Phase 3 (Decision 042 §"Renames") — `init service <name>` (formerly
// `scaffold service`). Adds a service to an existing entity's cloudgrid.yaml.
import { initServiceCommand } from './scaffold/service.js';
// BL621 Phase 3 (Decision 042 Mode 1 §init behavior b) — folder-rooted "gridify"
// mode. `cloudgrid init` with no <kind>/<name> prepares an existing app's
// cloudgrid.yaml without registering a server entity. See services/gridify.ts.
import { runGridify, type GridifyDeps } from '../services/gridify.js';

// Read the CLI version once at module load. We walk up from this file's
// directory until we find a package.json — this makes the lookup work for
// both the production build (`dist/index.js` → `dist/../package.json`) and
// tests running from `src/commands/init.ts` where the file lives two levels
// deeper. Mirrors the spirit of `index.ts:9-10` while being reorg-safe.
const __dirname = dirname(fileURLToPath(import.meta.url));
function findPkgVersion(startDir: string): string {
  let dir = startDir;
  for (let i = 0; i < 6; i++) {
    const candidate = join(dir, 'package.json');
    try {
      const pkg = JSON.parse(readFileSync(candidate, 'utf-8'));
      // The CLI's package.json — guard against accidentally hitting the
      // monorepo root or a parent shared/ folder by name-checking.
      if (pkg.name === '@cloudgrid-io/cli' && typeof pkg.version === 'string') {
        return pkg.version;
      }
    } catch {
      // Fall through to the parent.
    }
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return '0.0.0';
}
const PKG_VERSION: string = findPkgVersion(__dirname);

const ALLOWED_KINDS = ['app', 'agent'] as const;
type Kind = typeof ALLOWED_KINDS[number];

// Per decision 015's entry-point matrix. `cron` is intentionally out of the
// `--type` shortcut on init: cron services need a `--schedule` and rarely
// stand alone as the project's first service. The user can add one with
// `cloudgrid scaffold service <name> --type cron --schedule '<expr>'` after
// init completes.
const ALLOWED_INIT_TYPES = ['node', 'nextjs', 'python', 'static'] as const;
type InitServiceType = (typeof ALLOWED_INIT_TYPES)[number];

// Default service name when `--type` is given on init. Users who want a
// different name can rename later via `cloudgrid scaffold rename web <new>`.
// The choice matches the developer-flow doc's Step 2 example.
const DEFAULT_SERVICE_NAME = 'web';

// Spec v17 §6.1 slug rules live in commands/slug.ts (shared with the plug
// auto-init path). `suggestSlug` / `isValidSlug` are re-exported below so
// existing importers keep working.
import { SLUG_MAX, isValidSlug, suggestSlug } from './slug.js';

export { suggestSlug } from './slug.js';

export interface InitOpts extends GridOpts {
  description?: string;
  space?: string;
  dir?: string;
  /**
   * Optional service type. When set, init seeds `cloudgrid.yaml` with a
   * single `services.web: { type: <T> }` block AND scaffolds `services/web/`
   * with the per-type starter. When omitted, init writes an empty
   * `services: {}` block; the user adds services with `cloudgrid scaffold
   * service`.
   */
  type?: string;
  /**
   * When true, scaffold the entity as internal-only: yaml gets
   * `expose: false` and the seeded service (if `--type` is also given)
   * carries no `path:`. The entity opts out of public HTTP routing and
   * the routability validation in backlog 082 is satisfied without a
   * path. Per decision 020 §"Override path".
   */
  internal?: boolean;
  /**
   * QA-09 — when true, scaffold into the current directory instead of the
   * default `./<name>/` subdir. Equivalent to `--dir .` but discoverable:
   * the success block of a default (subdir) init points users here. `--here`
   * and an explicit `--dir` are mutually exclusive.
   */
  here?: boolean;
  /**
   * Skip the gridify confirmation prompt on the no-argument folder-rooted form
   * (`cloudgrid init` in an existing app). Mirrors `plug --auto`: a file write
   * is a real side effect, so non-interactive runs must opt in. Unused by the
   * explicit `init <kind> <name>` path.
   */
  auto?: boolean;
}

interface EntityResponse {
  entity_id: string;
  name: string;
  kind: 'app' | 'agent';
  realm: string;
  status: string;
  org_id: string;
  display_name?: string;
  created_at: string;
}

/**
 * Build the §6.5 "Try one of:" candidate list for a CONFLICT error.
 * Three candidates: `<slug>-2`, `<org-slug>-<slug>`, `<slug>-prod`. Any
 * candidate that exceeds SLUG_MAX or collides with the original input is
 * dropped. Returns an empty array if all candidates are filtered out —
 * the caller omits the line in that case.
 */
function conflictSuggestions(slug: string, orgSlug: string): string[] {
  const candidates = [`${slug}-2`, `${orgSlug}-${slug}`, `${slug}-prod`];
  return candidates.filter((c) => c.length <= SLUG_MAX && c !== slug);
}

/**
 * Map a V2ApiError from POST /api/v2/entities into the §6.5 friendly
 * CloudGridError. Codes that aren't recognised fall through to a generic
 * "Server error (status code)." message.
 */
function mapServerError(
  err: V2ApiError,
  ctx: { slug: string; orgSlug: string },
): CloudGridError {
  // 401-class — auth.
  if (
    err.code === 'AUTH_REQUIRED' ||
    err.code === 'AUTH_EXPIRED' ||
    err.code === 'AUTH_INVALID'
  ) {
    return new CloudGridError(notSignedInMessage());
  }

  // 403 — wrong role.
  if (err.code === 'FORBIDDEN_ROLE') {
    return new CloudGridError(
      'Creating an entity requires admin or developer role on this grid.',
    );
  }

  // 409 — slug taken (display_name uniqueness).
  if (err.code === 'CONFLICT') {
    const lines = [`'${ctx.slug}' is already taken in ${ctx.orgSlug}-hub.`];
    const suggestions = conflictSuggestions(ctx.slug, ctx.orgSlug);
    if (suggestions.length > 0) {
      lines.push(`Try one of: ${suggestions.join(', ')}`);
    }
    return new CloudGridError(lines.join('\n'));
  }

  // 400 — server-side slug/name validation. Pass through verbatim per brief.
  if (err.code === 'VALIDATION_FAILED') {
    return new CloudGridError(err.message);
  }

  // Generic fallback (5xx, INTERNAL_ERROR, UNKNOWN, anything else).
  return new CloudGridError(
    `Server error (${err.status} ${err.code}). Try again, or run cloudgrid feedback if it persists.`,
  );
}

/**
 * Inner runner — exported so tests can exercise the verb without going
 * through commander. Mirrors the pattern used by `runSpaceCreate` and
 * `runAdminInvite`.
 *
 * The optional `client` parameter is the M1 testability seam: production
 * callers omit it and `getV2Client(opts.org)` resolves a real V2Client;
 * tests pass a `MockV2Client`. Verbs MUST type the parameter as `IV2Client`,
 * not `V2Client`, per the M1 handoff.
 */
export async function runInit(
  kind: string,
  name: string,
  opts: InitOpts,
  client?: IV2Client,
): Promise<void> {
  if (!ALLOWED_KINDS.includes(kind as Kind)) {
    throw new CloudGridError(
      `Kind must be 'app' or 'agent'. Got: '${kind}'.`,
    );
  }

  // `--grid <slug>` is the primary context flag; `--org` is the deprecated,
  // still-resolving alias (grid-opt.ts). Read the effective value once.
  const grid = gridSlug(opts);

  if (!isValidSlug(name)) {
    const suggestion = suggestSlug(name);
    const lines = ['Slug must be lowercase alphanumeric with hyphens.'];
    if (suggestion) lines.push(`Try: ${suggestion}`);
    throw new CloudGridError(lines.join('\n'));
  }

  // Validate `--type` (when given). Reject early before any HTTP so a typo
  // doesn't leave a charged server-side entity behind.
  let serviceType: InitServiceType | null = null;
  if (opts.type !== undefined) {
    if (!ALLOWED_INIT_TYPES.includes(opts.type as InitServiceType)) {
      throw new CloudGridError(
        `--type must be one of: ${ALLOWED_INIT_TYPES.join(', ')}. Got: '${opts.type}'.`,
      );
    }
    serviceType = opts.type as InitServiceType;
  }

  // QA-09 — `--here` is a discoverable alias for `--dir .` (init into cwd).
  // Reject the contradictory combination rather than silently picking one.
  if (opts.here && opts.dir !== undefined) {
    throw new CloudGridError(
      'Pass either --here or --dir <path>, not both.',
    );
  }

  // Resolve the target directory. Default is `./<name>` relative to cwd;
  // `--dir` (or `--here`) overrides. The dir-exists check fires BEFORE the
  // POST so we don't orphan a charged server-side entity if the local write
  // would fail anyway. cwd-mode (--here, --dir ., or equivalent) skips it.
  const baseCwd = process.cwd();
  const effectiveDir = opts.here ? '.' : opts.dir;
  const targetDir = effectiveDir
    ? resolve(baseCwd, effectiveDir)
    : resolve(baseCwd, name);
  const isCwdMode = targetDir === baseCwd;
  // The default-subdir case is the QA-09 surprise: no dir override was asked
  // for, yet init created `./<name>/`. Flag it so the success block can tell
  // the user about `--here`.
  const usedDefaultSubdir = !isCwdMode && effectiveDir === undefined;

  if (!isCwdMode && existsSync(targetDir)) {
    const userPath = `./${opts.dir ? opts.dir.replace(/^\.\//, '') : name}`;
    throw new CloudGridError(
      [
        `Directory already exists: ${userPath}`,
        `Use: cloudgrid init ${kind} ${name} --dir <other-path>`,
      ].join('\n'),
    );
  }

  // In cwd-mode (--dir .), block if cloudgrid.yaml is already present —
  // we'd be overwriting an existing project. The dir-exists check above
  // already covers the non-cwd case.
  if (isCwdMode && existsSync(join(targetDir, 'cloudgrid.yaml'))) {
    throw new CloudGridError(
      [
        'cloudgrid.yaml already exists in this directory.',
        'Use a different directory, or remove the existing file first.',
      ].join('\n'),
    );
  }

  // Resolve client (test fixture or real). `getV2Client` itself throws
  // V2ApiError AUTH_REQUIRED if no JWT is on disk — caught and re-mapped
  // below alongside any server-emitted auth errors. Passing the resolved grid
  // through means the explicit override scopes the client too, so the
  // POST below carries the right `x-cloudgrid-org` header.
  let c: IV2Client;
  try {
    c = client ?? (await getV2Client(grid ?? undefined));
  } catch (err) {
    if (err instanceof V2ApiError) {
      throw mapServerError(err, { slug: name, orgSlug: grid ?? '' });
    }
    throw err;
  }

  // Active-grid resolution is server-authoritative: `getActiveOrg` calls
  // `/api/v2/grids` and matches either `--grid <slug>` (when provided) or
  // the persisted `active_org_slug` against the user's real memberships.
  // It throws CloudGridError with the right wording for each failure mode
  // (no slug set, slug stale, slug not a member, zero grids).
  let active: { org_id: string; slug: string; name: string };
  try {
    active = await getActiveOrg(c, { override: grid ?? null });
  } catch (err) {
    if (err instanceof V2ApiError) {
      throw mapServerError(err, { slug: name, orgSlug: grid ?? '' });
    }
    throw err;
  }
  const orgSlugForRender = active.slug;
  const activeOrgIdForLink = active.org_id;

  // POST /api/v2/entities. Server normalises slug, may suffix it.
  // `description` and `space_slug` are optional pass-throughs from the
  // matching CLI flags. The server tolerates additional fields today
  // (route at packages/api/src/routes/v2/entities.ts only validates kind
  // and name); plumbing them through now is forward-compat. Empty or
  // whitespace-only values are treated as unset — we don't send empty
  // strings. Sibling pattern: admin/invite.ts conditionally adds
  // `space_ids` and `note` the same way.
  const body: { kind: string; name: string; description?: string; space_slug?: string } = {
    kind,
    name,
  };
  const trimmedDescription = opts.description?.trim();
  if (trimmedDescription) body.description = trimmedDescription;
  const trimmedSpace = opts.space?.trim();
  if (trimmedSpace) body.space_slug = trimmedSpace;

  let entity: EntityResponse;
  try {
    entity = await c.post<EntityResponse>('/api/v2/entities', body);
  } catch (err) {
    if (err instanceof V2ApiError) {
      throw mapServerError(err, { slug: name, orgSlug: orgSlugForRender });
    }
    throw err;
  }

  // Create the target dir (no-op in cwd mode since cwd already exists).
  if (!isCwdMode) {
    mkdirSync(targetDir, { recursive: true });
  }

  // Write .cloudgrid/link.json. The shape is fixed and read by every
  // entity-targeted verb. Per decision 012 §4 the link carries `kind` so
  // verbs can branch on app/agent without an extra HTTP round-trip. Per
  // decision 015 §"Identity-aware behavior" the link also carries the
  // server-disambiguated `entity_slug` so the dev runner's `{app-name}`
  // substitution doesn't have to fall back to yaml `name:` or
  // `basename(cwd)` for newly-init'd projects.
  const link: LinkArtifact = {
    entity_id: entity.entity_id,
    org_id: activeOrgIdForLink,
    org_slug: orgSlugForRender,
    kind: entity.kind,
    entity_slug: entity.name,
    created_at: new Date().toISOString(),
    cli_version: PKG_VERSION,
  };
  writeLink(link, targetDir);

  // Append .cloudgrid/ + .cloudgrid-dev.lock + .env.local to .gitignore
  // (idempotent — M1 helper handles dedup). The lock file is per decision
  // 015 §"Lock file"; it tracks PID + per-service ports for the local
  // cloudgrid dev session. `.env.local` is per decision 018 — every
  // service's dotenv lives at `services/<name>/.env.local` and never gets
  // committed; the gitignore line at the project root catches all of them.
  appendGitignoreLine('.cloudgrid/', targetDir);
  appendGitignoreLine('.cloudgrid-dev.lock', targetDir);
  appendGitignoreLine('.env.local', targetDir);

  // Write the starter cloudgrid.yaml. With `--type <T>` the yaml seeds a
  // single `services.web` block of that type AND we scaffold the per-type
  // starter files into `services/web/`. Without `--type` we write an empty
  // services map; the user adds services with `cloudgrid scaffold service`.
  //
  // The empty form is `services: {}` (flow style) per kickoff 09 spec.
  // When a follow-up `cloudgrid scaffold service` adds the first entry,
  // `addService` flips the empty flow map to block style automatically (see
  // `addService`'s "promote empty flow map" branch) so the project ends up
  // with the conventional block layout, not `services: { web: ... }`.
  const yamlPath = join(targetDir, 'cloudgrid.yaml');
  writeFileSync(yamlPath, `name: ${entity.name}\nservices: {}\n`, 'utf-8');

  // Backlog 088 — `--internal` opts the entity out of public routing.
  // Run BEFORE `scaffoldFirstService` so `addService` rewrites a yaml that
  // already carries the `expose:` field; the alphabetical insert in
  // `addService` keeps services in order regardless of when expose was added.
  if (opts.internal) {
    setExposeFalse(targetDir);
  }

  const scaffoldedFolder = serviceType
    ? scaffoldFirstService(targetDir, serviceType, { internal: !!opts.internal })
    : null;

  // Build the success block. The format mirrors the canonical happy path
  // in `docs/strategy/developer-flow.md` Step 2: a `✓`-bulleted list of
  // what was created, followed by a `Charged. Not yet live.` status line,
  // followed by a `Next:` block with the most likely follow-up command.
  const displayName = entity.display_name ?? name;
  const linkedPath = isCwdMode
    ? '.cloudgrid/link.json'
    : `./${relative(baseCwd, targetDir)}/.cloudgrid/link.json`;
  const cdTarget = isCwdMode ? null : relative(baseCwd, targetDir);

  // Notice when the server returned a different slug than what the user
  // asked for (server-side collision; the API appended a suffix). Surfacing
  // this once on the success path beats a confused user wondering why
  // `acme` became `acme-7392` later.
  const slugChanged = entity.name !== name;

  const blockLines: string[] = [];
  blockLines.push(
    `Initialising ${displayName} (${entity.kind}) in ${orgSlugForRender}-hub...`,
  );
  if (slugChanged) {
    blockLines.push(
      `  Note: requested slug '${name}' was already taken in this grid; using '${entity.name}'.`,
    );
  }
  blockLines.push(`  ✓ Created cloudgrid.yaml`);
  blockLines.push(`  ✓ Created ${linkedPath}`);
  if (scaffoldedFolder) {
    const folderLabel = isCwdMode
      ? scaffoldedFolder
      : `./${relative(baseCwd, targetDir)}/${scaffoldedFolder}`;
    blockLines.push(`  ✓ Scaffolded ${folderLabel} (${serviceType} starter)`);
  }
  // QA-09 — when init created the default `./<name>/` subdir the user didn't
  // ask for, tell them about `--here` so they can opt into a cwd init next
  // time. Only fires on the implicit-subdir path (no --dir/--here override).
  if (usedDefaultSubdir) {
    blockLines.push(
      `  Created ./${cdTarget}/ — pass --here to use the current directory instead.`,
    );
  }
  blockLines.push('  Charged. Not yet live.');
  blockLines.push(`  Slug:    ${entity.name}`);
  blockLines.push(`  Grid:    ${orgSlugForRender}`);
  blockLines.push('');
  if (cdTarget) {
    blockLines.push(`  cd ${cdTarget}`);
  }
  if (scaffoldedFolder) {
    // With a service already scaffolded, `cloudgrid dev` is the next step.
    blockLines.push('  cloudgrid dev     # run locally');
    blockLines.push('  cloudgrid plug    # to take it live');
  } else {
    // No service yet — point at `init service` first, then plug.
    blockLines.push('  cloudgrid init service web --type <node|nextjs|python|static>');
    blockLines.push('  cloudgrid plug    # to take it live');
  }
  blockLines.push('');

  process.stdout.write(blockLines.join('\n'));
}

/**
 * Seed `services/<DEFAULT_SERVICE_NAME>/` with the per-type starter and
 * register it in `cloudgrid.yaml`. Returns the relative service folder
 * path (e.g. `services/web/`) for the success-block render.
 *
 * Reuses `addService` (yaml comment-preserving edit) and `scaffoldService`
 * (idempotent file writer) — same code paths `cloudgrid scaffold service`
 * runs, so the yaml ends up identical regardless of which command produced
 * it.
 *
 * Per backlog 088: the first service gets `path: /` by default so the
 * yaml passes backlog 082's routability check. With `--internal` (passed
 * as `opts.internal`) the path is omitted — the entity opts out of public
 * routing via `expose: false` set elsewhere in the yaml.
 */
function scaffoldFirstService(
  targetDir: string,
  type: ServiceType,
  opts: { internal: boolean } = { internal: false },
): string {
  const addOpts = opts.internal ? { type } : { type, path: '/' };
  addService(targetDir, DEFAULT_SERVICE_NAME, addOpts);
  scaffoldService(targetDir, DEFAULT_SERVICE_NAME, type);
  return `services/${DEFAULT_SERVICE_NAME}/`;
}

/**
 * The no-argument, folder-rooted form of `cloudgrid init` (Decision 042 Mode 1
 * §init). Decides between gridify and a greenfield pointer based on the cwd:
 *
 *   - cloudgrid.yaml already here        → tell the user it's ready to plug.
 *   - existing app code (framework seen) → gridify: write a reviewed yaml.
 *   - empty / unrecognised folder        → point at the explicit greenfield
 *                                          form `init <app|agent> <slug>`.
 *
 * Gridify never registers a server entity — the dir stays unlinked until the
 * first `cloudgrid plug`. The `deps` seam is for tests; production omits it.
 */
export async function runInitFolder(
  opts: { auto?: boolean },
  deps: GridifyDeps = {},
): Promise<void> {
  const cwd = process.cwd();
  if (existsSync(join(cwd, 'cloudgrid.yaml'))) {
    throw new CloudGridError(
      [
        'This folder already has a cloudgrid.yaml.',
        'Run cloudgrid plug to deploy it, or cloudgrid init service <name> --type <type> to add a service.',
      ].join('\n'),
    );
  }

  const result = await runGridify(cwd, { auto: opts.auto }, deps);
  if (result.kind === 'undetected') {
    throw new CloudGridError(
      [
        "Couldn't detect an app in this folder.",
        'To create a new entity, run: cloudgrid init <app|agent> <slug>',
      ].join('\n'),
    );
  }
  // `created` / `declined` / `exists` already printed their own line in
  // runGridify; nothing more to say here.
}

export const initCommand = new Command('init')
  .description("Set up the current folder, or register a new entity (app or agent)")
  .argument('[kind]', "Entity kind for the explicit form: 'app' or 'agent'")
  .argument('[name]', 'Slug (3-40 lowercase alphanumeric/hyphens, no leading/trailing hyphen)');
addGridOption(initCommand, 'Override the active grid for this init');
initCommand
  .option('--description <text>', 'Initial entity description')
  .option('--space <slug>', 'Associate the entity with a space')
  .option('--dir <path>', 'Target directory for scaffold (defaults to ./<name>)')
  .option('--here', 'Scaffold into the current directory instead of ./<name>')
  .option(
    '--type <type>',
    `Seed the project with a 'web' service of the given type (${ALLOWED_INIT_TYPES.join('|')})`,
  )
  .option(
    '--internal',
    "Mark the entity as internal-only: yaml gets 'expose: false' and the seeded service has no 'path:'.",
  )
  .option(
    '--auto',
    'No-argument form only: write cloudgrid.yaml for the detected app without a confirmation prompt.',
  )
  .action(async (kind: string | undefined, name: string | undefined, opts: InitOpts) => {
    const start = Date.now();
    // No <kind>/<name> → folder-rooted gridify (Decision 042 Mode 1 §init).
    if (kind === undefined && name === undefined) {
      await runInitFolder({ auto: opts.auto });
    } else if (name === undefined) {
      // `init app` with no slug — the explicit form needs both. Guide rather
      // than fall through to a confusing validation error.
      throw new CloudGridError(
        `init ${kind} needs a name. Example: cloudgrid init ${kind} my-app`,
      );
    } else {
      await runInit(kind as string, name, opts);
    }
    // Decision 016 — success event on a high-signal verb. Best-effort:
    // a telemetry-side bug must not turn a successful init into a failure.
    try {
      const { recordCliEvent } = await import('../services/telemetry.js');
      await recordCliEvent({
        event_type: 'success',
        command: 'cloudgrid init',
        elapsed_ms: Date.now() - start,
      });
    } catch { /* swallow */ }
  });

// `gridctl init service <name> --type <type>` — add a service to an existing
// entity. Commander matches the `service` subcommand before the `<kind>`
// argument, so `init app <name>` / `init agent <name>` are unaffected.
initCommand.addCommand(initServiceCommand);
